// PreferencesConfig.tsx
import React, { useState } from 'react';
import { Input } from '@/components/common/Input';
import { Select } from '@/components/common/Select';
import { Button } from '@/components/common/Button';
import { Plus, Trash2 } from 'lucide-react';

export const PreferencesConfig: React.FC = () => {
  const [activeSection, setActiveSection] = useState('display');
  const [preferences, setPreferences] = useState({
    // Display Preferences
    dateFormat: 'DD/MM/YYYY',
    defaultTimeUnit: 'Minutes',
    
    // Tax Preferences
    country: 'India',
    taxColumn1: 'CGST',
    taxColumn2: 'SGST',
    taxColumn3: 'IGST',
    
    // Custom Tax Configuration (for Other country)
    customTaxes: [
      { id: 1, name: 'VAT', percentage: 18 },
      { id: 2, name: 'Environmental Tax', percentage: 2 }
    ]
  });

  const sections = [
    { id: 'display', label: 'Display Preferences' },
    { id: 'tax', label: 'Tax Preferences' }
  ];

  const handleChange = (field: string, value: any) => {
    setPreferences(prev => ({ ...prev, [field]: value }));
  };

  const handleAddTax = () => {
    setPreferences(prev => ({
      ...prev,
      customTaxes: [...prev.customTaxes, { id: Date.now(), name: '', percentage: 0 }]
    }));
  };

  const handleUpdateTax = (id: number, field: string, value: any) => {
    setPreferences(prev => ({
      ...prev,
      customTaxes: prev.customTaxes.map(tax => 
        tax.id === id ? { ...tax, [field]: value } : tax
      )
    }));
  };

  const handleDeleteTax = (id: number) => {
    setPreferences(prev => ({
      ...prev,
      customTaxes: prev.customTaxes.filter(tax => tax.id !== id)
    }));
  };

  const renderDisplayPreferences = () => (
    <div className="space-y-8">
      {/* First Row: Section Heading + First Field */}
      <div className="grid grid-cols-3 gap-8">
        <div className="col-span-1">
          <h2 className="text-lg font-medium text-gray-900">Display Preferences</h2>
          <p className="text-sm text-gray-600 mt-2">
            Set preferences for how you want dates and time units to appear across the platform.
          </p>
        </div>
        <div className="col-span-1">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Date Format</h3>
            <p className="text-sm text-gray-500 mt-1">
              Choose how dates should appear across the app (dashboards, exports, filters, etc).
            </p>
          </div>
        </div>
        <div className="col-span-1">
          <Select
            value={preferences.dateFormat}
            onChange={(e) => handleChange('dateFormat', e.target.value)}
            className="w-full"
          >
            <option value="DD/MM/YYYY">DD/MM/YYYY</option>
            <option value="MM/DD/YYYY">MM/DD/YYYY</option>
            <option value="YYYY-MM-DD">YYYY-MM-DD</option>
          </Select>
        </div>
      </div>

      {/* Second Row: Empty + Second Field */}
      <div className="grid grid-cols-3 gap-8">
        <div className="col-span-1"></div>
        <div className="col-span-1">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Default Time Unit</h3>
            <p className="text-sm text-gray-500 mt-1">
              Set your preferred unit for time entries across tasks and reports.
            </p>
          </div>
        </div>
        <div className="col-span-1">
          <Select
            value={preferences.defaultTimeUnit}
            onChange={(e) => handleChange('defaultTimeUnit', e.target.value)}
            className="w-full"
          >
            <option value="Minutes">Minutes</option>
            <option value="Hours">Hours</option>
            <option value="Days">Days</option>
          </Select>
        </div>
      </div>
    </div>
  );

  const renderTaxPreferences = () => (
    <div className="space-y-8">
      {/* First Row: Section Heading + First Field */}
      <div className="grid grid-cols-3 gap-8">
        <div className="col-span-1">
          <h2 className="text-lg font-medium text-gray-900">Tax Preferences</h2>
          <p className="text-sm text-gray-600 mt-2">
            Customize how tax information appears on your quotations and invoices. Based on your selected country, set up the tax column labels to match your business needs.
          </p>
        </div>
        <div className="col-span-1">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Country</h3>
            <p className="text-sm text-gray-500 mt-1">
              Select the country to configure tax structure accordingly.
            </p>
          </div>
        </div>
        <div className="col-span-1">
          <Select
            value={preferences.country}
            onChange={(e) => handleChange('country', e.target.value)}
            className="w-full"
          >
            <option value="India">India</option>
            <option value="USA">USA</option>
            <option value="UK">UK</option>
            <option value="Other">Other</option>
          </Select>
        </div>
      </div>

      {/* Conditional rendering based on country */}
      {preferences.country === 'India' && (
        <>
          <div className="grid grid-cols-3 gap-8">
            <div className="col-span-1"></div>
            <div className="col-span-1">
              <div>
                <h3 className="text-sm font-medium text-gray-900">Tax Column #1</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Choose the first tax column label (e.g., CGST, VAT, etc.)
                </p>
              </div>
            </div>
            <div className="col-span-1">
              <Select
                value={preferences.taxColumn1}
                onChange={(e) => handleChange('taxColumn1', e.target.value)}
                className="w-full"
              >
                <option value="CGST">CGST</option>
                <option value="SGST">SGST</option>
                <option value="IGST">IGST</option>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-8">
            <div className="col-span-1"></div>
            <div className="col-span-1">
              <div>
                <h3 className="text-sm font-medium text-gray-900">Tax Column #2</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Choose the second tax column label (optional).
                </p>
              </div>
            </div>
            <div className="col-span-1">
              <Select
                value={preferences.taxColumn2}
                onChange={(e) => handleChange('taxColumn2', e.target.value)}
                className="w-full"
              >
                <option value="SGST">SGST</option>
                <option value="CGST">CGST</option>
                <option value="IGST">IGST</option>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-8">
            <div className="col-span-1"></div>
            <div className="col-span-1">
              <div>
                <h3 className="text-sm font-medium text-gray-900">Tax Column #3</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Choose the third tax column label (optional).
                </p>
              </div>
            </div>
            <div className="col-span-1">
              <Select
                value={preferences.taxColumn3}
                onChange={(e) => handleChange('taxColumn3', e.target.value)}
                className="w-full"
              >
                <option value="IGST">IGST</option>
                <option value="CGST">CGST</option>
                <option value="SGST">SGST</option>
              </Select>
            </div>
          </div>
        </>
      )}

      {preferences.country === 'Other' && (
        <div className="grid grid-cols-3 gap-8">
          <div className="col-span-1"></div>
          <div className="col-span-2">
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-1">
                Custom Tax Configuration
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                Define your own tax columns and rates if you're operating outside India.
              </p>

              <div className="space-y-4">
                {/* Existing Tax Rows */}
                {preferences.customTaxes.map((tax, index) => (
                  <div key={tax.id} className="grid grid-cols-2 gap-4">
                    <Input
                      value={tax.name}
                      onChange={(e) => handleUpdateTax(tax.id, 'name', e.target.value)}
                      placeholder="Tax Column Name"
                      className="w-full"
                    />
                    <div className="flex items-center space-x-2">
                      <Input
                        type="number"
                        value={tax.percentage}
                        onChange={(e) => handleUpdateTax(tax.id, 'percentage', parseFloat(e.target.value) || 0)}
                        placeholder="Tax Percentage (%)"
                        className="flex-1"
                      />
                      {preferences.customTaxes.length > 1 && (
                        <button
                          onClick={() => handleDeleteTax(tax.id)}
                          className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                {/* Empty Row for New Tax */}
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    placeholder="Tax Column Name"
                    className="w-full"
                    disabled
                  />
                  <Input
                    placeholder="Tax Percentage (%)"
                    className="w-full"
                    disabled
                  />
                </div>

                {/* Add Tax Button */}
                <Button
                  variant="secondary"
                  onClick={handleAddTax}
                  className="inline-flex items-center"
                >
                  <Plus size={16} className="mr-2" />
                  Add Tax Column
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'display':
        return renderDisplayPreferences();
      case 'tax':
        return renderTaxPreferences();
      default:
        return renderDisplayPreferences();
    }
  };

  const handleSave = () => {
    console.log('Saving preferences:', preferences);
  };

  return (
    <div className="h-full flex bg-gray-50">
      {/* Left Sidebar Navigation */}
      <div className="w-64 bg-white border-r border-gray-200">
        <nav className="p-4 space-y-1">
          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`w-full text-left px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeSection === section.id
                  ? 'bg-blue-50 text-blue-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              {section.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-6xl mx-auto px-8 py-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            {renderContent()}
            
            {/* Action Buttons */}
            {/* <div className="mt-12 pt-8 border-t border-gray-200 flex justify-end space-x-4">
              <Button 
                variant="secondary" 
                onClick={() => console.log('Reset preferences')}
              >
                Reset to Default
              </Button>
              <Button 
                variant="primary" 
                onClick={handleSave}
              >
                Save Changes
              </Button>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};